import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { parsePrefixedAddress } from '@safe-global/utils/utils/addresses'
import type { Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
import { Select, SelectTrigger, SelectValue } from '@safe-global/views/components/ui/select'
import { Skeleton } from '@safe-global/views/components/ui/skeleton'
import { cn } from '@safe-global/views/utils/cn'
import SafeSelectorTriggerContent from './components/SafeSelectorTriggerContent'
import SafeDropdownContainer from './components/SafeDropdownContainer'
import InlineRetryError from '@safe-global/views/components/common/InlineRetryError'
import { useSafeSelectorState } from './hooks/useSafeSelectorState'
import { useIsSafeBarControlDisabled } from '@/hooks/useIsSafeBarControlDisabled'
import { useTopbarOverlayElevation } from '@/hooks/useTopbarElevation'
import useChains from '@/hooks/useChains'
import { getSafeSelectorClassVariants } from '@safe-global/views/features/spaces/components/SafeSelectorDropdown/utils/classVariants'
import type {
  SafeItemData,
  SafeSelectorDropdownProps,
} from '@safe-global/views/features/spaces/components/SafeSelectorDropdown/types'
import { Tooltip, TooltipTrigger, TooltipContent } from '@safe-global/views/components/ui/tooltip'

// Keeps the dropdown trigger renderable when the current safe isn't in `items`.
function buildFallbackSafeItem(selectedItemId: string | undefined, chainConfigs: Chain[]): SafeItemData | null {
  if (!selectedItemId) return null
  const { prefix: chainId, address } = parsePrefixedAddress(selectedItemId)
  if (!chainId || !address) return null
  const chain = chainConfigs.find((c) => c.chainId === chainId)
  return {
    id: selectedItemId,
    name: '',
    address,
    threshold: 0,
    owners: 0,
    balance: '',
    isLoading: true,
    chains: [
      {
        chainId,
        chainName: chain?.chainName ?? '',
        chainLogoUri: chain?.chainLogoUri ?? null,
        shortName: chain?.shortName ?? '',
      },
    ],
  }
}

function SafeSelectorDropdownSkeleton() {
  return (
    <div className="w-full min-[430px]:w-auto min-[430px]:flex-1 min-[430px]:min-w-0 min-[430px]:max-w-[515px] h-10 flex items-center gap-4 rounded-lg py-0.5 pl-1.5 pr-2 bg-muted">
      <Skeleton className="size-8 shrink-0 rounded-full" />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton className="h-3.5 w-24 rounded-full" />
        <Skeleton className="h-3 w-32 rounded-full" />
      </div>
      <div className="flex flex-col items-end gap-1.5 pr-12">
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    </div>
  )
}

function SafeSelectorDropdown({
  items,
  listItems,
  selectedItemId,
  onItemSelect,
  isLoading,
  isError,
  onRetry,
  header,
  footer,
  emptyStateOverride,
  searchValue,
  onSearchValueChange,
  onItemRename,
  onReorder,
  keepOpen,
}: SafeSelectorDropdownProps) {
  const hasDropdownContent = Boolean(header) || Boolean(footer) || isLoading || isError
  // Force-openable so `isSingleSafe` can't hide the chevron when only one other safe exists.
  const willUseFallbackTrigger =
    items.length > 0 && Boolean(selectedItemId) && !items.some((item) => item.id === selectedItemId)
  const isDisabled = useIsSafeBarControlDisabled()
  const {
    dropdownOpen,
    selectedChainId,
    selectedItem,
    isSingleSafe,
    handleOpenChange,
    handleSafeChange,
    closeDropdown,
  } = useSafeSelectorState({
    items,
    selectedItemId,
    onItemSelect,
    forceOpenable: hasDropdownContent || willUseFallbackTrigger,
  })
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const variants = getSafeSelectorClassVariants(isSingleSafe)
  const isPopupOpen = variants.canOpen && !isDisabled && dropdownOpen
  const safeSelectValue = selectedItemId ?? selectedItem?.id
  const safeItemSelect = onItemSelect ?? (() => {})

  // The dropdown's backdrop dims the whole page; lift the topbar above it so the trigger stays lit.
  useTopbarOverlayElevation('safe-selector', isPopupOpen)

  const { configs: chainConfigs } = useChains()
  const fallbackSelectedItem = useMemo(
    () => (selectedItem ? null : buildFallbackSafeItem(selectedItemId, chainConfigs)),
    [selectedItem, selectedItemId, chainConfigs],
  )
  const triggerItem = selectedItem ?? fallbackSelectedItem

  // A lifted search query would otherwise persist across open/close (local state resets with the popup).
  const handleOpenChangeWithReset = (open: boolean) => {
    // Ignore close requests while a modal is layered on top (e.g. renaming): base-ui tries to close
    // the popup when focus/pointer moves into the dialog, but we want the user to keep their place.
    if (!open && keepOpen) return
    if (!open) onSearchValueChange?.('')
    handleOpenChange(open)
  }

  const triggerRef = useRef<HTMLButtonElement>(null)
  const wasOpenOnPressRef = useRef(false)

  // The display layer sits on top of the full-bleed trigger and its tooltip triggers must keep pointer
  // events (otherwise they never open on hover), so a press on the safe name or the balance lands on a
  // <span> the trigger behind it never sees. Forward it — unless it hit one of the row's own controls,
  // or base-ui already closed the popup on this same pointerdown, in which case forwarding reopens it.
  // Capture phase is required: when the popup is open base-ui dismisses it mid-pointerdown, so by the
  // bubble phase the state already reads closed and the guard below would let the click reopen it.
  const rememberOpenStateOnPress = () => {
    wasOpenOnPressRef.current = isPopupOpen
  }

  const forwardPressToTrigger = (event: MouseEvent<HTMLDivElement>) => {
    if (wasOpenOnPressRef.current || isDisabled || !variants.canOpen) return
    if (event.target instanceof Element && event.target.closest('a, button, [role="button"]')) return
    triggerRef.current?.click()
  }

  if (!mounted || !triggerItem) {
    if (isError && mounted) return <InlineRetryError message="Failed to load Safe data" onRetry={onRetry} />
    return <SafeSelectorDropdownSkeleton />
  }

  if (items.length === 0) {
    if (isError) return <InlineRetryError message="Failed to load Safe data" onRetry={onRetry} />
    return <SafeSelectorDropdownSkeleton />
  }

  const selectElement = (
    <Select
      value={safeSelectValue}
      onValueChange={handleSafeChange}
      open={isPopupOpen}
      onOpenChange={isDisabled ? undefined : handleOpenChangeWithReset}
      // base-ui highlights by moving DOM focus with the pointer, pulling it off the search input on
      // hover and on leaving the popup. Rows use :hover styles instead.
      highlightItemOnHover={false}
      // Deliberately not disabled: a disabled <button> blocks the inline address actions (copy,
      // explorer, env hint). Safe switching is prevented by the forced-closed `open` above instead.
    >
      <div
        className={cn(
          // The wrapper's overflow-hidden clips this focus-visible ring into stray top/bottom bars,
          // so suppress it — the card shows no focus ring by design (wrapper sets focus:ring-0).
          //
          // min-w-0 (trigger + value slot): without it a long safe name can't shrink/truncate and
          // pushes the balance and chevron out of the clipped card. Concretely: `flex-1` alone still
          // floors a flex item at min-content, so display content plus the paddings exceeded the
          // selector's fixed box and overflowed ~19px right; the trigger lays its chevron out with
          // `justify-end`, so the chevron rode that overflow into the gap and its padding ended up
          // under the nested-safes button. Letting both shrink keeps them inside.
          '-m-4 flex-1 min-w-0 w-full border-0 shadow-none bg-transparent dark:bg-transparent py-0 pl-4 hover:bg-transparent dark:hover:bg-transparent data-[state=open]:bg-transparent focus-visible:ring-0 focus-visible:border-0 [&_[data-slot=select-value]]:pr-0 [&_[data-slot=select-value]]:min-w-0 relative',
          variants.triggerClass,
          isDisabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {/* The trigger is an invisible full-bleed overlay BEHIND the display content, so the inline
            copy/explorer actions render outside the trigger <button> (no interactive nesting). */}
        <SelectTrigger
          ref={triggerRef}
          className={cn(
            // justify-end: the SelectValue is sr-only (out of flow), so the icon is the only flex
            // item and justify-between would park it at the left, behind the avatar.
            // eslint-disable-next-line no-restricted-syntax -- invisible full-bleed overlay trigger behind the card content (inset-0, ring/skin suppression); not a size/skin variant
            'absolute inset-0 z-0 h-auto w-auto justify-end border-0 bg-transparent p-0 hover:bg-transparent focus-visible:border-0 focus-visible:ring-0',
            isDisabled && 'cursor-not-allowed opacity-50',
          )}
          variant="ghost"
          iconWrapperClassName={variants.iconWrapperClass}
          aria-label={`Select Safe ${triggerItem.address}`}
          aria-disabled={isDisabled || undefined}
          data-testid="open-safes-icon"
        >
          <SelectValue className="sr-only">{triggerItem.address}</SelectValue>
        </SelectTrigger>
        <div
          onPointerDownCapture={rememberOpenStateOnPress}
          onClick={forwardPressToTrigger}
          className="relative z-10 flex h-full w-full pointer-events-none [&_[data-slot=tooltip-trigger]]:pointer-events-auto [&_[role=button]]:pointer-events-auto [&_a]:pointer-events-auto [&_button]:pointer-events-auto"
        >
          <SafeSelectorTriggerContent selectedItem={triggerItem} selectedChainId={selectedChainId} />
        </div>
      </div>

      <SafeDropdownContainer
        items={listItems ?? items}
        selectedItemId={safeSelectValue}
        onItemSelect={safeItemSelect}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        header={header}
        footer={footer}
        emptyStateOverride={emptyStateOverride}
        closeDropdown={closeDropdown}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        onItemRename={onItemRename}
        onReorder={onReorder}
      />
    </Select>
  )

  // TODO: change rounded-lg (8px) to rounded-2xl (16px) after migrating to the new design system
  // A muted 40px chip: the SpaceSafeBar pill that groups this with the nested-safes and
  // network controls carries the white card and its shadow.
  const wrapperClassName = cn(
    'group relative w-full min-[430px]:w-auto min-[430px]:flex-1 min-[430px]:min-w-0 min-[430px]:max-w-[515px] h-10 flex items-center rounded-lg py-0.5 pl-1.5 pr-2 overflow-hidden bg-muted focus:ring-0',
    variants.wrapperClass,
  )

  const innerContent = (
    <>
      <div className="pointer-events-none absolute inset-0 rounded-lg bg-muted-foreground/5 opacity-0 group-hover:opacity-100" />
      {selectElement}
    </>
  )

  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger render={<div data-testid="space-safes-navigation-block" className={wrapperClassName} />}>
          {innerContent}
        </TooltipTrigger>
        <TooltipContent side="bottom">Changing the Safe is not allowed in this screen</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <div data-testid="space-safes-navigation-block" className={wrapperClassName}>
      {innerContent}
    </div>
  )
}

export default SafeSelectorDropdown
export type { SafeSelectorDropdownProps }
